import { msg } from "../locales/translate.ts";
import { parseServerDate } from "../utils/dateTime.ts";

export const LIVENESS_SERVICES = ["processing", "notification", "gate", "cabinet", "tokenizer", "binlookup"] as const;

export type TLivenessServiceName = (typeof LIVENESS_SERVICES)[number];
export type TLiveness = {
	id: number;
	service: TLivenessServiceName;
	status: "success" | "failed";
	created_at: number | string;
	data: string | Record<string, unknown> | null;
};
export type TLivenessResponse = TLiveness[] | Record<string, TLiveness[]>;
export type TLivenessFilter = { service?: TLivenessServiceName; from_date: string; to_date: string };
export type LivenessState = TLiveness["status"] | "unknown";

const MILLISECONDS_PER_SECOND = 1000;
const MILLISECOND_TIMESTAMP_THRESHOLD = 100_000_000_000;
export const LIVENESS_PERIOD = 24 * 60 * 60 * 1000;

export function getRecentLiveness(records: TLiveness[], now = Date.now()) {
	return records.filter(check => {
		const time = livenessTimestamp(check.created_at);
		return time >= now - LIVENESS_PERIOD && time <= now;
	});
}

// The API returns ISO dates; retain support for older Unix timestamps.
export const livenessTimestamp = (value: number | string) => typeof value === "string" ? (parseServerDate(value)?.getTime() ?? NaN)
	: value < MILLISECOND_TIMESTAMP_THRESHOLD ? value * MILLISECONDS_PER_SECOND : value;

export function parseLivenessResponse(payload: unknown): TLiveness[] {
	let rows: unknown[];
	if (Array.isArray(payload)) rows = payload;
	else if (payload && typeof payload === "object" && Object.values(payload).every(Array.isArray)) rows = Object.values(payload).flat();
	else throw new Error(msg("unexpectedServiceStatusResponse"));

	const records = rows.map(row => {
		if (!row || typeof row !== "object") throw new Error(msg("invalidServiceStatusRecord"));
		const value = row as Record<string, unknown>;
		if (typeof value.id !== "number" || !Number.isFinite(value.id)
			|| !LIVENESS_SERVICES.includes(value.service as TLivenessServiceName)
			|| (value.status !== "success" && value.status !== "failed")
			|| (typeof value.created_at !== "number" && typeof value.created_at !== "string")
			|| livenessTimestamp(value.created_at) <= 0 || !Number.isFinite(livenessTimestamp(value.created_at))
			|| Number.isNaN(new Date(livenessTimestamp(value.created_at)).getTime())
			|| (value.data !== null && typeof value.data !== "string" && (typeof value.data !== "object" || Array.isArray(value.data)))) throw new Error(msg("invalidServiceStatusRecord"));
		return value as TLiveness;
	});
	return records.sort((a, b) => livenessTimestamp(b.created_at) - livenessTimestamp(a.created_at) || b.id - a.id);
}

export function getLivenessServices(records: TLiveness[]) {
	return LIVENESS_SERVICES.map(service => {
		const checks = records.filter(record => record.service === service);
		return { service, checks, latest: checks[0], state: checks[0]?.status ?? ("unknown" as const) };
	});
}

export function getLivenessState(records: TLiveness[]): LivenessState {
	const services = getLivenessServices(records);
	if (services.some(service => service.state === "failed")) return "failed";
	return services.every(service => service.state === "success") ? "success" : "unknown";
}

export function getLivenessTimeline(checks: TLiveness[], from: number, to: number, count = 48) {
	const duration = Math.max(1, to - from);
	const buckets = Array.from({ length: count }, (_, index) => ({
		from: from + duration * index / count,
		to: from + duration * (index + 1) / count,
		total: 0,
		failed: 0,
		state: "unknown" as LivenessState,
	}));
	for (const check of checks) {
		const timestamp = livenessTimestamp(check.created_at);
		if (timestamp < from || timestamp > to) continue;
		const index = Math.min(count - 1, Math.floor((timestamp - from) / duration * count));
		const bucket = buckets[index];
		bucket.total++;
		if (check.status === "failed") bucket.failed++;
		// A single failure must remain visible even among thousands of successful checks.
		bucket.state = bucket.failed ? "failed" : "success";
	}
	return buckets;
}

export function getLivenessDetails(data: TLiveness["data"]) {
	let raw: unknown = data;
	try { if (typeof data === "string" && data) raw = JSON.parse(data); } catch { /* Error responses can be plain text. */ }
	const object = (value: unknown): Record<string, unknown> => value !== null && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : {};
	const response = object(raw);
	const body = object(response.data);
	return {
		raw,
		httpStatus: typeof response.httpStatus === "number" ? response.httpStatus : null,
		responseTime: typeof body.responseTime === "number" && Number.isFinite(body.responseTime) ? body.responseTime : null,
		version: typeof body.version === "string" ? body.version : null,
		endpoint: typeof body.endpoint === "string" ? body.endpoint : null,
	};
}
